import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import * as XLSX from "xlsx";
import Navbar from "@/components/Navbar";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { ArrowLeft, Download } from "lucide-react";

function StatCard({ label, value, sub, note }) {
  return (
    <div className="bg-white border border-stone-200 rounded-2xl p-5 flex flex-col gap-0.5">
      <div className="text-[9px] tracking-[0.2em] uppercase text-stone-400 font-medium">{label}</div>
      <div className="font-display text-2xl text-stone-900 mt-0.5 leading-none">
        {value ?? <span className="text-stone-300">—</span>}
      </div>
      {sub && <div className="text-[11px] text-stone-400 mt-0.5 leading-snug">{sub}</div>}
      {note && <div className="text-[10px] text-stone-300 italic mt-0.5">{note}</div>}
    </div>
  );
}

function Badge({ yes }) {
  return yes ? (
    <span className="inline-block text-[9px] tracking-[0.12em] uppercase px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">
      Yes
    </span>
  ) : (
    <span className="inline-block text-[9px] tracking-[0.12em] uppercase px-2 py-0.5 rounded-full bg-stone-100 text-stone-400 border border-stone-200">
      No
    </span>
  );
}

export default function AdminUsageReport() {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState({ company: "", completed: "" });
  const [search, setSearch] = useState("");

  useEffect(() => {
    api
      .get("/admin/usage-report")
      .then((r) => setReport(r.data))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  }, []);

  const uniqueCompanies = useMemo(() => {
    if (!report) return [];
    return [...new Set(report.rows.map((r) => r.company).filter((c) => c !== "—"))].sort();
  }, [report]);

  const filtered = useMemo(() => {
    if (!report) return [];
    let rows = report.rows;
    if (filter.company) rows = rows.filter((r) => r.company === filter.company);
    if (filter.completed === "yes") rows = rows.filter((r) => r.completed);
    if (filter.completed === "no") rows = rows.filter((r) => !r.completed);
    if (search) {
      const q = search.toLowerCase();
      rows = rows.filter(
        (r) =>
          r.participant_name.toLowerCase().includes(q) ||
          r.participant_email.toLowerCase().includes(q) ||
          r.scenario.toLowerCase().includes(q) ||
          r.batch.toLowerCase().includes(q) ||
          r.company.toLowerCase().includes(q),
      );
    }
    return rows;
  }, [report, filter, search]);

  const exportExcel = () => {
    if (!report) return;
    const today = new Date().toISOString().slice(0, 10);

    const sessionRows = report.rows.map((r) => ({
      "Session ID": r.session_id,
      "Participant Name": r.participant_name,
      "Email": r.participant_email,
      "Employee ID": r.employee_id,
      "Company": r.company,
      "Batch / Program": r.batch,
      "Scenario": r.scenario,
      "Category": r.category,
      "Difficulty": r.difficulty,
      "Session Start": r.started_at ? new Date(r.started_at).toLocaleString() : "—",
      "Session End": r.ended_at ? new Date(r.ended_at).toLocaleString() : "—",
      "Duration (min)": r.duration_min ?? "—",
      "Text Messages": r.text_messages,
      "Voice Messages (from deploy date)": r.voice_messages,
      "Total User Messages": r.total_user_messages,
      "AI Responses": r.ai_responses,
      "Total Words Sent": r.total_words_sent,
      "Total Chars Sent": r.total_chars_sent,
      "Est. Tokens (chars÷4)": r.est_tokens,
      "Completed": r.completed ? "Yes" : "No",
      "Attempt #": r.attempt_number,
      "Overall Score": r.overall_score ?? "—",
    }));

    const s = report.summary;
    const summaryRows = [
      ["Metric", "Value", "Notes"],
      ["Total Participants", s.total_participants, "Unique users with at least one session"],
      ["Total Sessions", s.total_sessions, ""],
      ["Completed Sessions", s.completed_sessions, ""],
      ["Completion Rate (%)", s.completion_rate, ""],
      ["Unique Scenarios Attempted", s.total_unique_scenarios, ""],
      ["Avg Sessions per Participant", s.avg_sessions_per_participant, ""],
      ["", "", ""],
      ["Avg Duration (min)", s.avg_duration_min, "Completed sessions only"],
      ["Median Duration (min)", s.median_duration_min, ""],
      ["P75 Duration (min)", s.p75_duration_min, ""],
      ["P90 Duration (min)", s.p90_duration_min, ""],
      ["", "", ""],
      ["Avg Text Messages / Session", s.avg_text_messages, "Completed sessions only"],
      ["Avg Voice Messages / Session", s.avg_voice_messages, "Tracked from deploy date only"],
      ["Avg AI Responses / Session", s.avg_ai_interactions, ""],
      ["Avg Est. Tokens / Session", s.avg_est_tokens, "Estimated: total chars ÷ 4"],
      ["", "", ""],
      ["Text Usage (%)", s.pct_text, "Share of user messages sent via text"],
      ["Voice Usage (%)", s.pct_voice, "Share of user messages sent via voice"],
    ];

    const wb = XLSX.utils.book_new();
    const ws1 = XLSX.utils.json_to_sheet(sessionRows);
    ws1["!cols"] = [
      { wch: 26 }, { wch: 22 }, { wch: 30 }, { wch: 14 }, { wch: 20 },
      { wch: 20 }, { wch: 40 }, { wch: 18 }, { wch: 12 }, { wch: 20 },
      { wch: 20 }, { wch: 14 }, { wch: 14 }, { wch: 28 }, { wch: 18 },
      { wch: 14 }, { wch: 16 }, { wch: 16 }, { wch: 22 }, { wch: 12 },
      { wch: 10 }, { wch: 12 },
    ];
    XLSX.utils.book_append_sheet(wb, ws1, "Session Detail");

    const ws2 = XLSX.utils.aoa_to_sheet(summaryRows);
    ws2["!cols"] = [{ wch: 38 }, { wch: 16 }, { wch: 44 }];
    XLSX.utils.book_append_sheet(wb, ws2, "Summary");

    XLSX.writeFile(wb, `leadersim-usage-report-${today}.xlsx`);
    toast.success("Exported — check your downloads folder");
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-stone-50">
        <Navbar />
        <div className="max-w-7xl mx-auto px-6 py-16 text-stone-400 text-sm">
          Loading usage data…
        </div>
      </div>
    );
  }

  const s = report?.summary;

  return (
    <div className="min-h-screen bg-stone-50">
      <Navbar />
      <div className="max-w-7xl mx-auto px-6 sm:px-10 py-10">
        {/* Header */}
        <Link
          to="/admin/companies"
          className="inline-flex items-center text-sm text-stone-500 hover:text-stone-800 mb-6"
        >
          <ArrowLeft className="w-4 h-4 mr-1.5" strokeWidth={1.5} /> Back to Admin
        </Link>

        <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
          <div>
            <div className="text-[9px] tracking-[0.2em] uppercase text-stone-400 mb-1.5">
              Admin
            </div>
            <h1 className="font-display text-3xl md:text-4xl text-stone-900 tracking-tight leading-none">
              Usage Report
            </h1>
            <p className="text-sm text-stone-400 mt-2">
              {s?.total_sessions ?? 0} sessions · {s?.total_participants ?? 0} participants ·{" "}
              data as of{" "}
              {new Date().toLocaleDateString("en-IN", {
                day: "numeric",
                month: "short",
                year: "numeric",
              })}
            </p>
          </div>
          <Button
            data-testid="export-usage-xlsx-btn"
            onClick={exportExcel}
            className="bg-[#b81f24] hover:bg-[#9a1a1f] text-white rounded-md h-10 px-5"
          >
            <Download className="w-4 h-4 mr-1.5" strokeWidth={1.75} /> Export Excel
          </Button>
        </div>

        {/* Summary Stats */}
        {s && (
          <div className="space-y-3 mb-10">
            {/* Participation */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <StatCard
                label="Total Participants"
                value={s.total_participants}
                sub="Unique users with sessions"
              />
              <StatCard
                label="Total Sessions"
                value={s.total_sessions}
                sub={`${s.completed_sessions} completed (${s.completion_rate}%)`}
              />
              <StatCard
                label="Unique Scenarios Attempted"
                value={s.total_unique_scenarios}
              />
              <StatCard
                label="Avg Sessions / Participant"
                value={s.avg_sessions_per_participant}
              />
            </div>

            {/* Duration */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <StatCard
                label="Avg Duration"
                value={s.avg_duration_min != null ? `${s.avg_duration_min}m` : null}
                sub="Completed sessions"
              />
              <StatCard
                label="Median Duration"
                value={s.median_duration_min != null ? `${s.median_duration_min}m` : null}
              />
              <StatCard
                label="P75 Duration"
                value={s.p75_duration_min != null ? `${s.p75_duration_min}m` : null}
              />
              <StatCard
                label="P90 Duration"
                value={s.p90_duration_min != null ? `${s.p90_duration_min}m` : null}
              />
            </div>

            {/* Messages & Tokens */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <StatCard
                label="Avg Text Msgs / Session"
                value={s.avg_text_messages}
                sub="Completed sessions"
              />
              <StatCard
                label="Avg Voice Msgs / Session"
                value={s.avg_voice_messages}
                sub="Completed sessions"
                note="Tracked from deploy date"
              />
              <StatCard
                label="Avg AI Responses / Session"
                value={s.avg_ai_interactions}
              />
              <StatCard
                label="Avg Est. Tokens / Session"
                value={s.avg_est_tokens}
                sub="Completed sessions"
                note="Estimated: total chars ÷ 4"
              />
            </div>

            {/* Text vs Voice split */}
            <div
              className="bg-white border border-stone-200 rounded-2xl p-5"
              data-testid="usage-channel-split"
            >
              <div className="text-[9px] tracking-[0.2em] uppercase text-stone-400 mb-3 font-medium">
                Message Channel Split
              </div>
              <div className="flex items-center gap-4">
                <div className="flex-1 h-2.5 rounded-full overflow-hidden bg-stone-100">
                  <div
                    className="h-full bg-[#b81f24] rounded-full"
                    style={{ width: `${s.pct_text}%` }}
                  />
                </div>
                <div className="text-sm text-stone-600 whitespace-nowrap">
                  Text{" "}
                  <span className="font-semibold text-stone-900">{s.pct_text}%</span>
                  {" · "}
                  Voice{" "}
                  <span className="font-semibold text-stone-900">{s.pct_voice}%</span>
                </div>
              </div>
              {s.pct_voice === 0 && (
                <p className="text-[10px] text-stone-300 mt-2 italic">
                  Voice tracking was enabled after initial deployment — historical sessions show 0 voice messages.
                </p>
              )}
            </div>
          </div>
        )}

        {/* Filter bar */}
        <div className="flex gap-3 mb-4 flex-wrap items-center">
          <Input
            data-testid="usage-search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search participant, scenario, batch…"
            className="h-10 max-w-sm rounded-md"
          />
          <select
            data-testid="usage-filter-company"
            value={filter.company}
            onChange={(e) => setFilter((f) => ({ ...f, company: e.target.value }))}
            className="h-10 rounded-md border border-stone-200 bg-white px-3 text-sm text-stone-700 focus-visible:outline-none"
          >
            <option value="">All companies</option>
            {uniqueCompanies.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
          <select
            data-testid="usage-filter-status"
            value={filter.completed}
            onChange={(e) => setFilter((f) => ({ ...f, completed: e.target.value }))}
            className="h-10 rounded-md border border-stone-200 bg-white px-3 text-sm text-stone-700 focus-visible:outline-none"
          >
            <option value="">All sessions</option>
            <option value="yes">Completed only</option>
            <option value="no">Incomplete only</option>
          </select>
          <span className="text-sm text-stone-400">
            {filtered.length} row{filtered.length !== 1 ? "s" : ""}
          </span>
        </div>

        {/* Session Table */}
        {filtered.length === 0 ? (
          <div
            className="bg-white border border-dashed border-stone-200 rounded-3xl p-16 text-center"
            data-testid="usage-empty-state"
          >
            <p className="text-stone-400 text-sm">No sessions match the current filters.</p>
          </div>
        ) : (
          <div
            className="bg-white border border-stone-200 rounded-2xl overflow-hidden"
            data-testid="usage-sessions-table"
          >
            <div className="overflow-x-auto">
              <table className="w-full text-sm min-w-[1200px]">
                <thead className="bg-stone-50 text-[9px] tracking-[0.18em] uppercase text-stone-400 sticky top-0 z-10">
                  <tr>
                    <th className="text-left px-4 py-3 font-medium">Participant</th>
                    <th className="text-left px-4 py-3 font-medium">Company / Batch</th>
                    <th className="text-left px-4 py-3 font-medium">Scenario</th>
                    <th className="text-left px-4 py-3 font-medium">Start</th>
                    <th className="text-right px-4 py-3 font-medium">Duration</th>
                    <th className="text-right px-4 py-3 font-medium">Text</th>
                    <th className="text-right px-4 py-3 font-medium">Voice</th>
                    <th className="text-right px-4 py-3 font-medium">AI</th>
                    <th className="text-right px-4 py-3 font-medium">Words</th>
                    <th className="text-right px-4 py-3 font-medium">~Tokens</th>
                    <th className="text-center px-4 py-3 font-medium">Done</th>
                    <th className="text-center px-4 py-3 font-medium">Try #</th>
                    <th className="text-right px-4 py-3 font-medium">Score</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((r) => (
                    <tr
                      key={r.session_id}
                      className="border-t border-stone-100 hover:bg-stone-50/70 transition-colors"
                      data-testid={`usage-row-${r.session_id}`}
                    >
                      <td className="px-4 py-3">
                        <div className="font-medium text-stone-900 text-sm leading-none">
                          {r.participant_name}
                        </div>
                        <div className="text-[11px] text-stone-400 mt-0.5">{r.participant_email}</div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-stone-700 text-sm leading-none">{r.company}</div>
                        <div className="text-[11px] text-stone-400 mt-0.5 truncate max-w-[160px]">
                          {r.batch}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-stone-800 text-sm truncate max-w-[220px] leading-none">
                          {r.scenario}
                        </div>
                        <div className="text-[11px] text-stone-400 mt-0.5">{r.category}</div>
                      </td>
                      <td className="px-4 py-3 text-[11px] text-stone-400 whitespace-nowrap">
                        {r.started_at
                          ? new Date(r.started_at).toLocaleString("en-IN", {
                              day: "2-digit",
                              month: "short",
                              hour: "2-digit",
                              minute: "2-digit",
                            })
                          : "—"}
                      </td>
                      <td className="px-4 py-3 text-right font-mono text-xs text-stone-700">
                        {r.duration_min != null ? `${r.duration_min}m` : "—"}
                      </td>
                      <td className="px-4 py-3 text-right text-stone-800 font-medium">
                        {r.text_messages}
                      </td>
                      <td className="px-4 py-3 text-right text-stone-500">
                        {r.voice_messages > 0 ? (
                          r.voice_messages
                        ) : (
                          <span className="text-stone-300">0</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right text-stone-700">{r.ai_responses}</td>
                      <td className="px-4 py-3 text-right text-stone-500">{r.total_words_sent}</td>
                      <td className="px-4 py-3 text-right text-stone-500">{r.est_tokens}</td>
                      <td className="px-4 py-3 text-center">
                        <Badge yes={r.completed} />
                      </td>
                      <td className="px-4 py-3 text-center text-xs text-stone-500">
                        {r.attempt_number > 1 ? (
                          <span className="font-semibold text-amber-600">#{r.attempt_number}</span>
                        ) : (
                          <span className="text-stone-400">#1</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right font-display text-base text-stone-900">
                        {r.overall_score ?? <span className="text-stone-300">—</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        <p className="text-[10px] text-stone-300 mt-4 text-right italic">
          Token estimates are approximate (total characters ÷ 4). Voice tracking captures messages sent via push-to-talk from the date this feature was enabled.
        </p>
      </div>
    </div>
  );
}
